import { formatStableDate } from '@/helpers/ssr/dates'

/**
 * Shared formatting utilities for opportunity data
 */

/**
 * Format an ISO date string into a human-readable format
 * @example formatDate('2024-01-15T00:00:00Z') // 'Jan 15, 2024'
 */
export function formatDate(isoDate: string | null | undefined): string | null {
  return formatStableDate(isoDate)
}

/**
 * Format currency amount with USD formatting
 * @example formatCurrency(50000) // '$50,000'
 */
export function formatCurrency(
  amount: number | null | undefined,
): string | null {
  if (amount == null) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format currency in compact notation for large amounts
 * @example formatCompactCurrency(1500000) // '$1.5M'
 */
export function formatCompactCurrency(
  amount: number | null | undefined,
): string | null {
  if (amount == null) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount)
}

/**
 * Format file size into human-readable string
 * @example formatFileSize(1048576) // '1.0 MB'
 */
export function formatFileSize(bytes: number): string
export function formatFileSize(bytes: number | null | undefined): string | null
export function formatFileSize(
  bytes: number | null | undefined,
): string | null {
  if (bytes == null) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Format business type/category strings into human-readable format
 * Handles both code formats ('category_business') and descriptive names ('FOR PROFIT ORGANIZATION')
 * @example formatBusinessType('category_business') // 'Category: Business'
 * @example formatBusinessType('FOR PROFIT ORGANIZATION') // 'For Profit Organization'
 * @example formatBusinessType('CORPORATE ENTITY (NOT TAX EXEMPT)') // 'Corporate Entity (Not Tax Exempt)'
 */
export function formatBusinessType(type: string | null | undefined): string {
  if (!type) return ''

  // Check if it has 'category_' prefix and preserve it with proper formatting
  const hasCategory = type.toLowerCase().startsWith('category_')

  // Remove common prefixes like 'category_' temporarily
  let formatted = type.replace(/^category_/i, '')

  // Replace underscores with spaces
  formatted = formatted.replace(/_/g, ' ')

  // Convert to title case (capitalize first letter of each word)
  formatted = formatted
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())

  // Keep "US" capitalized when it appears as a standalone word
  formatted = formatted.replace(/\bUs\b/g, 'US')

  // Re-add Category prefix if it was there originally
  if (hasCategory) {
    formatted = `Category: ${formatted}`
  }

  return formatted
}
